import { z } from 'zod';
import { ActSourceSelectionSnapshotSchema } from '../act-catalog/index.js';

/**
 * Workers — composable base schemas.
 * Each processor-specific schema extends one of these bases via `.extend(...)`
 * and keeps `.passthrough()` during the DEBT-061..067 migration window
 * (tightening tracked in DEBT-069 — consolidated ticket with J+60 deadline).
 */

/**
 * Envelope BullMQ passed by the enqueuing route to `queue.add()`.
 * Used by 5 of the 7 processors (bordereau's 2 payloads carry the bordereau_id
 * directly and don't follow this envelope).
 */
export const BullMQPayloadBaseSchema = z.object({
  jobId: z.string(),
  requestId: z.string().optional(),
  userId: z.string().nullable().optional(),
});
export type BullMQPayloadBase = z.infer<typeof BullMQPayloadBaseSchema>;

/**
 * Fields persisted by every processor on the Redis job record.
 * Populated by `jobManager.updateJob`/`updateSynthesisJob`/`updateTimelineJob`
 * and the outer catch `handleJobFailure`.
 */
export const JobRedisStateBaseSchema = z.object({
  status: z
    .enum(['pending', 'processing', 'completed', 'partially_completed', 'failed', 'cancelled'])
    .optional(),
  stage: z.string().optional(),
  progress: z.number().optional(),
  created_at: z.union([z.string(), z.number()]).optional(),
  updated_at: z.union([z.string(), z.number()]).optional(),
  completed_at: z.union([z.string(), z.number()]).optional(),
  failedAt: z.string().optional(),
  failed_at: z.number().optional(),
  error: z.string().nullable().optional(),
  errorCode: z.string().optional(),
  errorMessage: z.string().optional(),
  workflow_type: z.string().optional(),
});
export type JobRedisStateBase = z.infer<typeof JobRedisStateBaseSchema>;

/**
 * Fields shared by the 3 document-generation paths (Path 1 research-only,
 * Path 2 generation-only, Path 3 generate-document). Each path extends this
 * with its own extras.
 */
/** Borne du tableau `montants_valides` : une ligne par poste, l'exposé reste dans la réponse à l'avocat. */
export const MONTANTS_VALIDES_MAX_CHARS = 8000;

/** Tableau de chiffrage validé par l'avocat, reporté tel quel par le rédacteur (`workers/services/montants_block.ts`). */
export const MontantsValidesSchema = z.string().trim().max(MONTANTS_VALIDES_MAX_CHARS);

export const RequestParamsBaseSchema = z.object({
  templateType: z.string().optional(),
  procedureType: z.string().optional(),
  additionalInstructions: z.string().optional(),
  specialisation: z.string().optional(),
  sourceSelection: ActSourceSelectionSnapshotSchema.optional(),
  cabinet_id: z.string().uuid().optional(),
  user_id: z.string().nullable().optional(),
  documentLength: z.enum(['short', 'medium', 'long', 'standard']).optional(),
  montants_valides: MontantsValidesSchema.optional(),
});
export type RequestParamsBase = z.infer<typeof RequestParamsBaseSchema>;
